import { Logger } from '../utils/Logger';

/**
 * Service centralisé pour gérer la navigation liée à l'authentification
 *
 * Responsabilités :
 * - Détecter le type de route (publique/protégée)
 * - Gérer les redirections en cas de 401
 * - Bloquer l'accès aux pages publiques si déjà connecté
 * - Centraliser toute la logique de navigation auth
 */
export class AuthNavigationService {
  /**
   * Routes publiques accessibles uniquement si NON connecté
   * (login, forgot-password, reset-password)
   */
  private readonly publicOnlyRoutes = [
    '/account/login',
    '/account/forgot-password',
    '/account/reset-password'
  ];

  /**
   * Vérifie si une route est publique (accessible sans authentification)
   */
  isPublicRoute(pathname: string): boolean {
    return this.publicOnlyRoutes.some(route => pathname.startsWith(route));
  }

  /**
   * Vérifie si une route est protégée (nécessite authentification)
   */
  isProtectedRoute(pathname: string): boolean {
    return !this.isPublicRoute(pathname);
  }

  /**
   * Retourne l'URL de redirection pour une erreur 401 (non authentifié)
   *
   * Règles :
   * - Route publique : null (pas de redirection nécessaire)
   * - Route protégée : rediriger vers login avec returnUrl
   *
   * @param currentPath Le chemin actuel (pathname + search)
   * @returns L'URL de redirection ou null si pas de redirection nécessaire
   */
  getRedirectUrlFor401(currentPath: string): string | null {
    const pathname = currentPath.split('?')[0];

    if (this.isPublicRoute(pathname)) {
      // Route publique : normal de ne pas être authentifié
      Logger.info('AuthNavigationService', `401 on public route ${pathname}, no redirect needed`);
      return null;
    }

    // Route protégée : rediriger vers login
    Logger.warn('AuthNavigationService', `401 on protected route ${pathname}, redirecting to login`);

    const returnUrl = pathname !== '/account/login' ? currentPath : '/';
    const loginUrl = `/account/login?returnUrl=${encodeURIComponent(returnUrl)}`;

    Logger.debug('AuthNavigationService', `Redirect URL for 401: ${loginUrl}`);
    return loginUrl;
  }

  /**
   * Retourne l'URL de redirection pour un utilisateur authentifié
   * qui tente d'accéder à une route publique
   *
   * Règles :
   * - Pages publiques (login, forgot-password, reset-password) : rediriger vers /
   * - Pages protégées : null (accès autorisé)
   *
   * @param pathname Le chemin de la route
   * @returns L'URL de redirection ou null si accès autorisé
   */
  getRedirectUrlForAuthenticatedOnPublic(pathname: string): string | null {
    if (this.isPublicRoute(pathname)) {
      Logger.info('AuthNavigationService', `Authenticated user accessing public route ${pathname}, redirecting to home`);
      return '/';
    }

    // User connecté sur route protégée : OK
    return null;
  }

  /**
   * Détermine la page de redirection par défaut après login
   * Basé sur le returnUrl dans l'URL ou page d'accueil
   *
   * Validation: refuse les routes publiques (login, logout, etc.)
   * pour éviter des redirections infinies
   *
   * @returns L'URL vers laquelle rediriger après login
   */
  getPostLoginRedirect(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl');

    // Extract pathname from returnUrl (remove query string)
    const pathname = returnUrl ? returnUrl.split('?')[0] : '';

    // Reject if returnUrl is a public route (login, logout, forgot-password, reset-password)
    if (returnUrl && !this.isPublicRoute(pathname)) {
      Logger.debug('AuthNavigationService', `Post-login redirect: ${returnUrl}`);
      return returnUrl;
    }

    if (returnUrl && this.isPublicRoute(pathname)) {
      Logger.warn('AuthNavigationService', `Post-login redirect: ${returnUrl} is a public route, redirecting to / instead`);
    }

    Logger.debug('AuthNavigationService', 'Post-login redirect: / (default)');
    return '/';
  }

}

// Instance singleton
export const authNavigationService = new AuthNavigationService();
